import React, { useCallback, useRef } from 'react';
import { Box, Button, Slider, Stack, Typography } from '@mui/material';
import {
  DEFAULT_PARTNER_LOGO_CIRCLE_LAYOUT,
  normalizePartnerLogoCircleLayout,
  PARTNER_LOGO_CIRCLE_FRAME,
  PARTNER_LOGO_CIRCLE_LAYER,
  PARTNER_LOGO_CIRCLE_SLOT,
  type PartnerLogoCircleLayout,
} from '../constants/partnerLogoCircle';

type Props = {
  logoUrl: string;
  providerName: string;
  backgroundColor?: string;
  layout: PartnerLogoCircleLayout;
  onChange: (layout: PartnerLogoCircleLayout) => void;
  disabled?: boolean;
};

function PartnerLogoCirclePreview({
  logoUrl,
  providerName,
  layout,
  backgroundColor,
  onDrag,
  disabled,
}: {
  logoUrl: string;
  providerName: string;
  layout: PartnerLogoCircleLayout;
  backgroundColor?: string;
  onDrag?: (deltaXPercent: number, deltaYPercent: number) => void;
  disabled?: boolean;
}) {
  const slotRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ x: number; y: number; offsetX: number; offsetY: number } | null>(null);
  const onDragRef = useRef(onDrag);
  onDragRef.current = onDrag;

  const startDrag = (e: React.PointerEvent) => {
    if (disabled || !onDragRef.current) return;
    e.preventDefault();
    dragRef.current = {
      x: e.clientX,
      y: e.clientY,
      offsetX: layout.offsetX,
      offsetY: layout.offsetY,
    };

    const handleMove = (ev: PointerEvent) => {
      const drag = dragRef.current;
      const slot = slotRef.current;
      const dragFn = onDragRef.current;
      if (!drag || !slot || !dragFn) return;
      const rect = slot.getBoundingClientRect();
      if (rect.width < 1 || rect.height < 1) return;
      const dx = ((ev.clientX - drag.x) / rect.width) * 100;
      const dy = ((ev.clientY - drag.y) / rect.height) * 100;
      dragFn(drag.offsetX + dx, drag.offsetY + dy);
    };

    const handleEnd = () => {
      dragRef.current = null;
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleEnd);
      window.removeEventListener('pointercancel', handleEnd);
    };

    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleEnd);
    window.addEventListener('pointercancel', handleEnd);
  };

  return (
    <Box
      sx={{
        position: 'relative',
        width: 280,
        maxWidth: '100%',
        aspectRatio: '355 / 372',
        mx: 'auto',
        bgcolor: backgroundColor || '#EBC41D',
        borderRadius: 1,
        flexShrink: 0,
        overflow: 'visible',
      }}
    >
      <Box
        component="img"
        src={PARTNER_LOGO_CIRCLE_FRAME}
        alt=""
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'block',
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          zIndex: PARTNER_LOGO_CIRCLE_LAYER.frame,
          pointerEvents: 'none',
        }}
      />
      <Box
        ref={slotRef}
        onPointerDown={startDrag}
        sx={{
          position: 'absolute',
          top: PARTNER_LOGO_CIRCLE_SLOT.top,
          left: PARTNER_LOGO_CIRCLE_SLOT.left,
          transform: 'translate(-50%, -50%)',
          width: PARTNER_LOGO_CIRCLE_SLOT.width,
          height: PARTNER_LOGO_CIRCLE_SLOT.height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'visible',
          zIndex: PARTNER_LOGO_CIRCLE_LAYER.logo,
          cursor: disabled ? 'default' : onDrag ? 'grab' : 'default',
          touchAction: 'none',
          '&:active': onDrag && !disabled ? { cursor: 'grabbing' } : undefined,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            transform: `translate(${layout.offsetX}%, ${layout.offsetY}%) scale(${layout.scale})`,
            transformOrigin: 'center center',
            pointerEvents: 'none',
          }}
        >
          <Box
            component="img"
            src={logoUrl}
            alt={providerName}
            sx={{
              maxWidth: '100%',
              maxHeight: '100%',
              width: 'auto',
              height: 'auto',
              objectFit: 'contain',
            }}
          />
        </Box>
      </Box>
    </Box>
  );
}

const PartnerLogoCircleEditor: React.FC<Props> = ({
  logoUrl,
  providerName,
  backgroundColor,
  layout,
  onChange,
  disabled,
}) => {
  const resolved = normalizePartnerLogoCircleLayout(layout);

  const applyDrag = useCallback(
    (offsetX: number, offsetY: number) => {
      onChange(
        normalizePartnerLogoCircleLayout({
          ...resolved,
          offsetX,
          offsetY,
        })
      );
    },
    [onChange, resolved]
  );

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        Glissez le logo dans le cercle pour le positionner. Utilisez le zoom pour ajuster la taille.
        L&apos;aperçu correspond au rendu sur la page publique partenaire.
      </Typography>

      <PartnerLogoCirclePreview
        logoUrl={logoUrl}
        providerName={providerName}
        layout={resolved}
        backgroundColor={backgroundColor}
        onDrag={disabled ? undefined : applyDrag}
        disabled={disabled}
      />

      <Box sx={{ maxWidth: 360, mx: 'auto', width: '100%', px: 1 }}>
        <Typography variant="caption" color="text.secondary" gutterBottom display="block">
          Zoom ({Math.round(resolved.scale * 100)}&nbsp;%)
        </Typography>
        <Slider
          value={resolved.scale}
          min={0.25}
          max={3}
          step={0.05}
          disabled={disabled}
          onChange={(_, v) =>
            onChange(normalizePartnerLogoCircleLayout({ ...resolved, scale: v as number }))
          }
          valueLabelDisplay="auto"
          valueLabelFormat={(v) => `${Math.round(v * 100)}%`}
        />
      </Box>

      <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap">
        <Button
          size="small"
          variant="outlined"
          disabled={disabled}
          onClick={() => onChange({ ...DEFAULT_PARTNER_LOGO_CIRCLE_LAYOUT })}
        >
          Réinitialiser position
        </Button>
      </Stack>
    </Stack>
  );
};

export default PartnerLogoCircleEditor;
